import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { validateProfilePhotoUrl } from '@/utils/photoUrlValidator';

export interface ProfilePhoto {
  id: string;
  url: string;
  type: 'main' | 'gallery';
  userId: string;
  username?: string;
}

export interface ChatMedia {
  id: string;
  url: string;
  type: 'image' | 'audio';
  messageId: string;
  conversationId: string;
  senderId: string;
  senderUsername?: string;
  createdAt: string;
}

export type UserTypeFilter = 
  | 'all'           // Todos os Usuários (excluindo teste)
  | 'sponsors'      // role = 'sponsor' (excluindo teste)
  | 'sponsored'     // role = 'sponsored' (excluindo teste)
  | 'free'          // plan_type IN free variants (excluindo teste)
  | 'standard'      // plan_type = sponsor_standard (excluindo teste)
  | 'premium'       // plan_type IN premium variants (excluindo teste)
  | 'active'        // onboarding_completed = true (excluindo teste)
  | 'pending'       // onboarding_completed = false (excluindo teste)
  | 'hidden'        // hidden = true (excluindo teste)
  | 'admins'        // user_roles admin/moderator (excluindo teste)
  | 'test';         // username LIKE 'test-%'

interface UsePaginatedMediaDataProps {
  mediaType: 'profile' | 'chat';
  userTypeFilter: UserTypeFilter;
  pageSize?: number;
}

interface UsePaginatedMediaDataReturn {
  items: (ProfilePhoto | ChatMedia)[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  totalCount: number;
}

const processProfilePhotos = (profiles: any[]): ProfilePhoto[] => {
  const photos: ProfilePhoto[] = [];
  
  // Processar em batches de 50 perfis por vez para otimização
  const BATCH_SIZE = 50;
  
  for (let i = 0; i < profiles.length; i += BATCH_SIZE) {
    const batch = profiles.slice(i, i + BATCH_SIZE);
    
    batch.forEach(profile => {
      const allUrls: string[] = [];
      
      if (profile.main_photo_url) {
        allUrls.push(profile.main_photo_url);
      }
      
      if (profile.profile_photos && Array.isArray(profile.profile_photos)) {
        profile.profile_photos.forEach((url: string) => {
          if (url && typeof url === 'string') {
            allUrls.push(url);
          }
        });
      }
      
      // Deduplicate URLs
      const uniqueUrls = [...new Set(allUrls)];
      
      uniqueUrls.forEach((url, index) => {
        photos.push({
          id: `${profile.id}-${index}`,
          url,
          type: url === profile.main_photo_url ? 'main' : 'gallery',
          userId: profile.id,
          username: profile.username
        });
      });
    });
  }
  
  return photos;
};

// Removed unused processChatMedia function - filtering now done inline

export function usePaginatedMediaData({
  mediaType,
  userTypeFilter,
  pageSize = 10
}: UsePaginatedMediaDataProps): UsePaginatedMediaDataReturn {
  const [items, setItems] = useState<(ProfilePhoto | ChatMedia)[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const isFetchingRef = useRef(false);

  const fetchBatch = useCallback(async (pageNum: number) => {
    if (isFetchingRef.current) {
      debugLogger.debug('[PaginatedMediaData] Already fetching, skipping...');
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    
    try {
      const start = pageNum * pageSize;
      const end = start + pageSize - 1;

      debugLogger.debug('[PaginatedMediaData] Fetching batch:', { 
        mediaType, 
        userTypeFilter, 
        pageNum, 
        start, 
        end 
      });

      if (mediaType === 'profile') {
        // Query para profile photos
        let query = supabase
          .from('profiles')
          .select('id, profile_photos, main_photo_url, username, onboarding_completed', { count: 'exact' })
          .not('profile_photos', 'is', null)
          .not('main_photo_url', 'is', null)
          .order('created_at', { ascending: false })
          .range(start, end);

        // Aplicar filtros - excluir teste de todos exceto 'test'
        switch (userTypeFilter) {
          case 'test':
            query = query.ilike('username', 'test-%');
            break;
          case 'sponsors':
            query = query.eq('role', 'sponsor').not('username', 'ilike', 'test-%');
            break;
          case 'sponsored':
            query = query.eq('role', 'sponsored').not('username', 'ilike', 'test-%');
            break;
          case 'free':
            query = query.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']).not('username', 'ilike', 'test-%');
            break;
          case 'standard':
            query = query.eq('plan_type', 'sponsor_standard').not('username', 'ilike', 'test-%');
            break;
          case 'premium':
            query = query.in('plan_type', ['sponsor_premium', 'sponsored_premium']).not('username', 'ilike', 'test-%');
            break;
          case 'active':
            query = query.eq('onboarding_completed', true).not('username', 'ilike', 'test-%');
            break;
          case 'pending':
            query = query.eq('onboarding_completed', false).not('username', 'ilike', 'test-%');
            break;
          case 'hidden':
            query = query.eq('hidden', true).not('username', 'ilike', 'test-%');
            break;
          case 'admins':
            // Buscar IDs de user_roles primeiro
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('role', ['admin', 'moderator']);
            const adminIds = roleData?.map(r => r.user_id) || [];
            if (adminIds.length > 0) {
              query = query.in('id', adminIds).not('username', 'ilike', 'test-%');
            } else {
              // Se não há admins, retornar vazio
              query = query.eq('id', '00000000-0000-0000-0000-000000000000');
            }
            break;
          case 'all':
          default:
            // Excluir contas de teste do "Todos"
            query = query.not('username', 'ilike', 'test-%');
            break;
        }

        const { data, count, error } = await query;
        
        if (error) throw error;
        
        // Processar fotos (expandir profile_photos array)
        const photos = processProfilePhotos(data || []);
        
        setItems(prev => pageNum === 0 ? photos : [...prev, ...photos]);
        setHasMore(data?.length === pageSize);
        setTotalCount(count || 0);
        
        debugLogger.debug('[PaginatedMediaData] Profile photos loaded:', {
          photosCount: photos.length,
          profilesCount: data?.length,
          totalCount: count
        });
      } else {
        // Query para chat media - buscar sem JOIN primeiro
        let query = supabase
          .from('messages')
          .select('id, media_url, content_type, conversation_id, sender_id, created_at', { count: 'exact' })
          .not('media_url', 'is', null)
          .eq('content_type', 'image')
          .order('created_at', { ascending: false })
          .range(start, end);

        const { data, count, error } = await query;
        
        if (error) throw error;
        
        // Debug: identificar mensagens com dados faltando
        if (data && data.length > 0) {
          const invalidMessages = data.filter(msg => !msg.conversation_id || !msg.sender_id);
          if (invalidMessages.length > 0) {
            debugLogger.warn('[PaginatedMediaData] Found messages with missing data:', {
              count: invalidMessages.length,
              samples: invalidMessages.slice(0, 3)
            });
          }
        }
        
        // Buscar perfis dos senders para filtrar
        const senderIds = [...new Set((data || []).map(msg => msg.sender_id))];
        
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, username, onboarding_completed, role, plan_type, hidden')
        .in('id', senderIds);

      debugLogger.debug('[PaginatedMediaData] Profiles fetched for chat media:', {
        senderIds,
        profilesCount: profilesData?.length,
        userTypeFilter
      });
        
        // Tipo para profile data
        interface ProfileMapData {
          id: string;
          username?: string;
          onboarding_completed?: boolean;
          role?: string;
          plan_type?: string;
          hidden?: boolean;
        }

        // Criar map de profiles
        const profilesMap = new Map<string, ProfileMapData>(
          (profilesData || []).map(p => [p.id, p as ProfileMapData])
        );
        
        // Filtrar mensagens baseado no userTypeFilter - excluir teste de todos exceto 'test'
        let filteredData = (data || []).filter(msg => {
          const profile = profilesMap.get(msg.sender_id);
          if (!profile) return false;
          
          const isTestAccount = profile.username?.toLowerCase().startsWith('test-');
          
          switch (userTypeFilter) {
            case 'test':
              return isTestAccount;
            case 'sponsors':
              return !isTestAccount && profile.role === 'sponsor';
            case 'sponsored':
              return !isTestAccount && profile.role === 'sponsored';
            case 'free':
              return !isTestAccount && ['free', 'sponsor_free', 'sponsored_free'].includes(profile.plan_type || '');
            case 'standard':
              return !isTestAccount && profile.plan_type === 'sponsor_standard';
            case 'premium':
              return !isTestAccount && ['sponsor_premium', 'sponsored_premium'].includes(profile.plan_type || '');
            case 'active':
              return !isTestAccount && profile.onboarding_completed === true;
            case 'pending':
              return !isTestAccount && profile.onboarding_completed === false;
            case 'hidden':
              return !isTestAccount && profile.hidden === true;
            case 'admins':
              // Para chat media, não suportamos filtro de admin (requer JOIN complexo)
              return false;
            case 'all':
            default:
              return !isTestAccount;
          }
        });
        
        // Processar mensagens com info de perfil (filtrar mensagens inválidas primeiro)
        const media = filteredData
          .filter(msg => msg.conversation_id && msg.sender_id) // Garantir dados válidos
          .map(msg => ({
            id: msg.id,
            url: msg.media_url || '',
            type: msg.content_type as 'image' | 'audio',
            messageId: msg.id,
            conversationId: msg.conversation_id,
            senderId: msg.sender_id,
            senderUsername: profilesMap.get(msg.sender_id)?.username,
            createdAt: msg.created_at || new Date().toISOString()
          }));
        
        setItems(prev => pageNum === 0 ? media : [...prev, ...media]);
        setHasMore(data?.length === pageSize);
        setTotalCount(count || 0);
        
        debugLogger.debug('[PaginatedMediaData] Chat media loaded:', {
          mediaCount: media.length,
          totalCount: count
        });
      }
    } catch (error) {
      debugLogger.error('[PaginatedMediaData] Error fetching batch:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [mediaType, userTypeFilter, pageSize]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || isFetchingRef.current) return;
    
    debugLogger.debug('[PaginatedMediaData] Loading more...', { currentPage: page });
    await fetchBatch(page + 1);
    setPage(prev => prev + 1);
  }, [loading, hasMore, page, fetchBatch]);

  const refetch = useCallback(async () => {
    debugLogger.debug('[PaginatedMediaData] Refetching from start');
    setPage(0);
    setItems([]);
    setHasMore(true);
    await fetchBatch(0);
  }, [fetchBatch]);

  // Carregar primeira página automaticamente
  useEffect(() => {
    refetch();
  }, [mediaType, userTypeFilter]);

  return {
    items,
    loading,
    hasMore,
    loadMore,
    refetch,
    totalCount
  };
}
